/**
 * Client authentication and session management for FreeFlash.
 * Supports HttpOnly cookie transport, credentials forwarding, and CSRF defense.
 */

import { API_BASE_URL } from '../config';

const TOKEN_KEY = 'freeflash_auth_token';
const USER_KEY = 'freeflash_user_info';

export function getToken() {
    return localStorage.getItem(TOKEN_KEY) || null;
}

export function setToken(token) {
    if (token) {
        localStorage.setItem(TOKEN_KEY, token);
    } else {
        localStorage.removeItem(TOKEN_KEY);
    }
}

export function getUser() {
    try {
        const item = localStorage.getItem(USER_KEY);
        return item ? JSON.parse(item) : null;
    } catch {
        return null;
    }
}

export function setUser(user) {
    if (user) {
        localStorage.setItem(USER_KEY, JSON.stringify(user));
    } else {
        localStorage.removeItem(USER_KEY);
    }
}

export function clearAuth() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
}

export function isAuthenticated() {
    return Boolean(getUser() || getToken());
}

export function isAdmin() {
    const user = getUser();
    return Boolean(user && (user.is_admin || user.isAdmin));
}

/**
 * Enhanced fetch wrapper that:
 * 1. Always sets credentials: 'include' for HttpOnly session cookie transmission.
 * 2. Attaches X-Requested-With header to trigger browser CORS preflights and protect against CSRF.
 * 3. Fallback Bearer Authorization header if token exists in localStorage.
 * 4. Automatically redirects to '/' if 401 Unauthorized occurs on protected routes.
 */
export async function authFetch(url, options = {}) {
    const headers = {
        'X-Requested-With': 'XMLHttpRequest',
        ...(options.headers || {})
    };
    const token = getToken();

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    if (options.body && typeof options.body === 'string' && !headers['Content-Type']) {
        headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(url, {
        ...options,
        credentials: 'include',
        headers
    });

    // If backend reports unauthenticated / session expired
    if (response.status === 401) {
        const isAuthEndpoint = (
            url.includes('/api/login') ||
            url.includes('/api/new-user') ||
            url.includes('/api/register') ||
            url.includes('/api/user/me')
        );
        if (!isAuthEndpoint) {
            clearAuth();
            const publicPaths = ['/', '/login', '/get-started', '/terms-of-service', '/terms'];
            if (!publicPaths.includes(window.location.pathname)) {
                window.location.href = '/';
            }
        }
    }

    return response;
}

/**
 * Perform server logout to clear HttpOnly session cookie and clear local user cache.
 */
export async function logoutUser() {
    try {
        await authFetch(`${API_BASE_URL}/api/logout`, { method: 'POST' });
    } catch (e) {
        console.error('Logout error:', e);
    } finally {
        clearAuth();
        window.location.href = '/';
    }
}
